import React, { useState } from 'react';
import PlaidConnectButton from './PlaidConnectButton';
import InstallPrompt from './InstallPrompt';
import LogoMark from './LogoMark';

export default function OnboardingWizard({ onFinish }) {
  const [step, setStep] = useState(2); // step 1 (account info) is already done
  const [connected, setConnected] = useState(false);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <LogoMark size={28} />
          <span style={styles.logoText}>Boring Budgeting</span>
        </div>

        <StepIndicator step={step} />

        {step === 2 && (
          <div style={styles.stepContent}>
            <h1 style={styles.heading}>Connect your accounts</h1>
            <p style={styles.subtext}>
              Link your bank and credit cards so your budget builds itself.
              This is the only real setup step - everything else is optional.
            </p>

            {connected ? (
              <div style={styles.successBox}>✓ Account connected</div>
            ) : (
              <PlaidConnectButton
                onSuccess={() => setConnected(true)}
                label="Connect Bank Account"
                style={styles.primaryBtn}
              />
            )}

            <button style={styles.continueBtn} onClick={() => setStep(3)}>
              {connected ? 'Continue →' : "I'll do this later →"}
            </button>
          </div>
        )}

        {step === 3 && (
          <div style={styles.stepContent}>
            <h1 style={styles.heading}>You're all set!</h1>
            <p style={styles.subtext}>
              Your dashboard is ready. If you want to fine-tune things later,
              you can add manual transactions, planned expenses (like
              property tax or an annual bill), and more - but none of that
              is required to get started.
            </p>

            <InstallPrompt />

            <button style={styles.primaryBtn} onClick={onFinish}>
              Go to Dashboard →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepIndicator({ step }) {
  const steps = [
    { n: 1, label: 'Your Info' },
    { n: 2, label: 'Connect' },
    { n: 3, label: 'Done' },
  ];

  return (
    <div style={styles.stepper}>
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div style={styles.stepItem}>
            <div style={{
              ...styles.stepCircle,
              ...(s.n < step ? styles.stepDone : s.n === step ? styles.stepCurrent : {}),
            }}>
              {s.n < step ? '✓' : s.n}
            </div>
            <span style={styles.stepLabel}>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ ...styles.stepLine, ...(s.n < step ? styles.stepLineDone : {}) }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a0f1e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
  },
  card: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '20px',
    padding: '2rem',
    maxWidth: '440px',
    width: '100%',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.75rem',
  },
  logoText: {
    fontSize: '1.1rem',
    fontWeight: '800',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  stepper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.35rem',
  },
  stepCircle: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '1px solid #374151',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  stepDone: {
    background: '#00d4aa',
    borderColor: '#00d4aa',
    color: '#0a0f1e',
  },
  stepCurrent: {
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  stepLabel: {
    color: '#6b7280',
    fontSize: '0.65rem',
    fontFamily: "'Syne', sans-serif",
  },
  stepLine: {
    flex: 1,
    height: '1px',
    background: '#1e2a3a',
    marginBottom: '1.1rem',
  },
  stepLineDone: { background: '#00d4aa' },
  stepContent: {},
  heading: {
    color: '#fff',
    fontSize: '1.3rem',
    fontWeight: '800',
    fontFamily: "'Syne', sans-serif",
    margin: '0 0 0.6rem',
  },
  subtext: {
    color: '#9ca3af',
    fontSize: '0.85rem',
    lineHeight: '1.6',
    marginBottom: '1.5rem',
  },
  primaryBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '10px',
    padding: '0.85rem',
    fontSize: '0.9rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    width: '100%',
    marginBottom: '1rem',
  },
  continueBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: 'none',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    width: '100%',
    textAlign: 'center',
    padding: '0.5rem',
  },
  successBox: {
    background: 'rgba(0, 212, 170, 0.1)',
    border: '1px solid #00d4aa',
    borderRadius: '10px',
    padding: '0.85rem',
    color: '#00d4aa',
    fontSize: '0.85rem',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: '1rem',
    fontFamily: "'Syne', sans-serif",
  },
};
